import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { cachedGet } from "./apiCache.js";
import "./styles.css";
import TodayPage from "./_pg_monitor.js";
import DetailsPage from "./_pg_details.js";
import TodayPLPage from "./_pg_compare.js";
import StickyNav from "./StickyNav.js";
import TradeUpload from "./_pg_upload.js";
import PlanScreen from "./_pg_plan.js";
import LoginPage from "./_pg_login.js";
import TagsPage from "./_pg_tags.js";
import PatternReco from "./_pg_patterns.js";
import LabsPage from "./_pg_admin.js"
import OptionsPage from "./_pg_options.js";
import FuturesPage from "./_pg_futures.js";
import _44_user_register from "./_44_user_register.js";
import { analyticsHandler } from "./eventLogger.js";
import AuthScreen from "./authscreen.js";
import { useNavigate } from "react-router-dom";
import TrialExpiringBanner from "./_89_trial_expiring_banner.js";
import { DateFilterProvider } from "./_40b_date_filter_context.js";
import { DateRangeProvider } from "./dateRangeContext.js";
import { TagFilterProvider } from "./TagFilterContext.js";
import { handleApiError, withErrorHandling } from "./utl/errorHandler.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";

const API_URL = process.env.REACT_APP_API_URL;
if (!API_URL) {
    throw new Error("REACT_APP_API_URL is not set. Please define it in your environment.");
}

const MODULE_NAME = "App.js";

if (!sessionStorage.getItem("plMethod")) {
    sessionStorage.setItem("plMethod", "fifo");
}

function isLoggedIn() {
    return !!sessionStorage.getItem("account_type");
}

const validScreens = {
    "pg_monitor": "pg_monitor",
    "intra-day": "pg_monitor",
    "monitor": "pg_monitor",
    "pg_details": "pg_details",
    "trades": "pg_details",
    "details": "pg_details",
    "pg_compare": "pg_compare",
    "compare": "pg_compare",
    "pg_upload": "pg_upload",
    "upload": "pg_upload",
    "pg_plan": "pg_plan",
    "risk-mgmt": "pg_plan",
    "plan": "pg_plan",
    "pg_patterns": "pg_patterns",
    "patterns": "pg_patterns",
    "pg_labs": "pg_labs",
    "labs": "pg_labs",
    "pg_tags": "pg_tags",
    "tags": "pg_tags",
    "pg_futures": "pg_futures",
    "pg_options": "pg_options",
    "checklist": "checklist"
};

function normalizeScreen(screenParam) {
    if (!screenParam) return null;
    const normalized = validScreens[screenParam.toLowerCase()];
    return normalized || null;
}

function getInitialScreen() {
    const saved = sessionStorage.getItem("currentScreen");
    const accountType = sessionStorage.getItem("account_type");

    if (saved === "pg_labs" && accountType !== "admin") {
        return "pg_details";
    }
    return saved || "pg_details";
}

function getInitialActiveDate() {
    return sessionStorage.getItem("activeDate") || null;
}

function useDebugMode() {
    const [debugMode, setDebugMode] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.code === 'KeyD') {
                e.preventDefault();
                setDebugMode(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (debugMode) {
            document.documentElement.classList.add('debug-mode-enabled');
            injectDebugLabels();
        } else {
            document.documentElement.classList.remove('debug-mode-enabled');
            removeDebugLabels();
        }
    }, [debugMode]);

    const injectDebugLabels = () => {
        document.querySelectorAll('[title]').forEach(el => {
            const tooltip = el.getAttribute('title');
            const match = tooltip.match(/^(\d+)/);
            if (match) {
                const componentId = match[1];
                if (!el.querySelector('.debug-component-id')) {
                    const label = document.createElement('span');
                    label.className = 'debug-component-id';
                    label.textContent = `#${componentId}`;
                    label.title = tooltip;
                    el.appendChild(label);
                }
            }
        });
    };

    const removeDebugLabels = () => {
        document.querySelectorAll('.debug-component-id').forEach(el => el.remove());
    };

    return { debugMode, setDebugMode };
}

function AppContent({ screen, setScreen, setSticknavEnabled, sticknavEnabled, navigationDisabled, noRecords, ...props }) {
    const location = useLocation();
    const activeModule = getActiveModule();

    // Sync URL pathname with screen state
    useEffect(() => {
        const path = location.pathname.replace(/^\/+|\/+$/g, '');
        const normalizedScreen = normalizeScreen(path);

        if (normalizedScreen && screen !== normalizedScreen) {
            const accountType = sessionStorage.getItem("account_type");

            if (normalizedScreen === "pg_labs" && accountType !== "admin") {
                // Redirect admin-only screen
                setScreen("pg_monitor");
                sessionStorage.setItem("currentScreen", "pg_monitor");
                window.history.replaceState({}, "", "/pg_monitor");
            } else {
                // Valid screen from URL
                setScreen(normalizedScreen);
                sessionStorage.setItem("currentScreen", normalizedScreen);
            }
            return;
        }

        // Empty path - use noRecords logic
        if (path === '' || path === 'index' || path === 'index.html') {
            if (noRecords) {
                setScreen("pg_upload");
                sessionStorage.setItem("currentScreen", "pg_upload");
                window.history.replaceState({}, "", "/pg_upload");
                return;
            }

            const saved = sessionStorage.getItem("currentScreen") || "pg_details";
            if (screen !== saved) {
                setScreen(saved);
                window.history.replaceState({}, "", `/${saved}`);
            }
        }
    }, [location.pathname, setScreen, noRecords]);

    let ScreenComponent;
    if (screen === "pg_monitor") {
        ScreenComponent = <TodayPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_details") {
        ScreenComponent = <DetailsPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_compare") {
        ScreenComponent = <TodayPLPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_tags") {
        ScreenComponent = <TagsPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_plan") {
        ScreenComponent = <PlanScreen {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_labs") {
        ScreenComponent = <LabsPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_patterns") {
        ScreenComponent = <PatternReco {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_futures") {
        ScreenComponent = <FuturesPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_options") {
        ScreenComponent = <OptionsPage {...props} screen={screen} setScreen={setScreen} />;
    } else if (screen === "pg_upload") {
        ScreenComponent = (
            <TradeUpload
                onNavigate={setScreen}
                setSticknavEnabled={setSticknavEnabled}
                sticknav={sticknavEnabled}
                screen={screen}
                {...props}
            />
        );
    } else {
        ScreenComponent = <TodayPage {...props} screen={screen} setScreen={setScreen} />;
    }

    // Determine if sticky nav should be visible
    // Hide it if a specific module is selected
    const shouldShowStickyNav = !activeModule;

    return (
        <div className="main-dash-container">
            <div style={{ paddingBottom: 70 }}>
                {ScreenComponent}
            </div>
            {shouldShowStickyNav && (
                <StickyNav
                    onNavigate={setScreen}
                    currentScreen={screen}
                    disabled={navigationDisabled}
                    refreshKey={props.refreshKey}
                />
            )}
        </div>
    );
}

function App() {
    const [screen, setScreen] = useState(getInitialScreen());
    const [loggedIn, setLoggedIn] = useState(isLoggedIn());
    const [activeDate, setActiveDate] = useState(null);
    const [checkedPLSummary, setCheckedPLSummary] = useState(false);
    const [latestDate, setLatestDate] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [navigationDisabled, setNavigationDisabled] = useState(false);
    const [noRecords, setNoRecords] = useState(false);
    const [sticknavEnabled, setSticknavEnabled] = useState(true);

    const { debugMode, setDebugMode } = useDebugMode();

    useEffect(() => {
        const handleStorage = () => setLoggedIn(isLoggedIn());
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    useEffect(() => {
        sessionStorage.setItem("currentScreen", screen);
    }, [screen]);

    useEffect(() => {
        if (activeDate) {
            sessionStorage.setItem("activeDate", activeDate);
        } else {
            sessionStorage.removeItem("activeDate");
        }
    }, [activeDate]);

    useEffect(() => {
        if (loggedIn && !checkedPLSummary) {
            withErrorHandling(
                () => cachedGet(`${process.env.REACT_APP_API_URL}/api/check-daily-pl-summary`, { refreshKey }),
                "Check Daily PL Summary",
                (friendlyError) => {
                    console.warn(`User-facing error: ${friendlyError}`);
                }
            )
                .then(res => {
                    if (res.data && "latest_date" in res.data) {
                        setLatestDate(res.data.latest_date);
                        if (res.data.latest_date) {
                            sessionStorage.setItem("latest_date", res.data.latest_date);
                        }
                    }

                    if (res.data && res.data.status === "no records") {
                        setNoRecords(true);
                        setScreen("pg_upload");
                    } else if (res.data && res.data.status === "records found") {
                        setNoRecords(false);
                    }

                    setCheckedPLSummary(true);
                })
                .catch((err) => {
                    setCheckedPLSummary(true);
                });
        }
    }, [loggedIn, refreshKey]);

    const handleLoginSuccess = (accountType, screenParam) => {
        sessionStorage.setItem("account_type", accountType);
        if (screenParam) {
            setScreen(screenParam);
            sessionStorage.setItem("currentScreen", screenParam);
        }
        setLoggedIn(true);
        setCheckedPLSummary(false);
    };

    const handleUploadSuccess = () => {
        setNavigationDisabled(false);
        setRefreshKey(prev => prev + 1);
        if (screen !== "pg_details") {
            setScreen("pg_details");
        }
    };

    useEffect(() => {
        if (loggedIn && sessionStorage.getItem("account_type") === "admin") {
            fetch(`${API_URL}/api/admin/init-user-tag-group`, { method: "POST", credentials: "include" })
                .then(res => res.json())
                .catch(err => console.error("[App.js] Tag group init failed:", err));

            fetch(`${API_URL}/api/admin/sync-tag-groups-from-patterns`, { method: "POST", credentials: "include" })
                .then(res => res.json())
                .catch(err => console.error("[App.js] Sync failed:", err));
        }
    }, [loggedIn]);

    useEffect(() => {
        function globalAnalyticsListener(e) {
            const tag = e.target.tagName?.toLowerCase();
            const type = e.target.type?.toLowerCase();
            let moduleEl = e.target.closest("[data-analytics]");
            let module_name = moduleEl ? moduleEl.getAttribute("data-analytics") : undefined;
            let subcomponent = undefined;

            if ((["select", "input", "button", "a", "div", "span"].includes(tag)) && typeof e.target.id === "string" && e.target.id.trim().length > 0) {
                subcomponent = e.target.id.trim();
            }

            if (tag === "input" && (type === "checkbox" || type === "radio")) {
                analyticsHandler({ event_type: `${type}_change`, module_name, subcomponent })(e);
            } else if (tag === "a") {
                analyticsHandler({ event_type: "link_click", module_name, subcomponent })(e);
            } else if (tag === "select") {
                analyticsHandler({ event_type: "dropdown_change", module_name, subcomponent })(e);
            } else if (tag === "button") {
                analyticsHandler({ event_type: "button_click", module_name, subcomponent })(e);
            } else if ((tag === "div" || tag === "span") && e.target.getAttribute("data-accordion") === "true") {
                analyticsHandler({ event_type: "accordion_expand", module_name, subcomponent })(e);
            }
        }

        document.addEventListener("click", globalAnalyticsListener, true);
        document.addEventListener("change", globalAnalyticsListener, true);
        return () => {
            document.removeEventListener("click", globalAnalyticsListener, true);
            document.removeEventListener("change", globalAnalyticsListener, true);
        };
    }, []);

    if (!loggedIn) {
        return (
            <div className="main-dash-container">
                <BrowserRouter>
                    <Routes>
                        <Route path="/auth" element={<AuthScreen onLoginSuccess={handleLoginSuccess} onNavigate={setScreen} />} />
                        <Route path="*" element={<LoginPage onLoginSuccess={handleLoginSuccess} onNavigate={setScreen} />} />
                    </Routes>
                </BrowserRouter>
            </div>
        );
    }

    if (!checkedPLSummary) {
        return (
            <div className="main-dash-container" style={{ color: "#fff", padding: 32 }}>
                Checking account status...
            </div>
        );
    }

    return (
        <TagFilterProvider>
            <DateRangeProvider>
                <BrowserRouter>
                    <DateFilterProvider>
                        <Routes>
                            <Route path="/pg_patterns/*" element={<PatternReco setScreen={setScreen} refreshKey={refreshKey} />} />
                            <Route path="/pg_labs/*" element={<LabsPage setScreen={setScreen} />} />
                            <Route path="/*" element={<AppContent
                                screen={screen}
                                setScreen={setScreen}
                                setSticknavEnabled={setSticknavEnabled}
                                sticknavEnabled={sticknavEnabled}
                                activeDate={activeDate}
                                setActiveDate={setActiveDate}
                                latest_date={latestDate}
                                refreshKey={refreshKey}
                                navigationDisabled={navigationDisabled}
                                setNavigationEnabled={setNavigationDisabled}
                                onUploadSuccess={handleUploadSuccess}
                                noRecords={noRecords}
                            />} />
                        </Routes>
                    </DateFilterProvider>
                </BrowserRouter>
            </DateRangeProvider>
        </TagFilterProvider>
    );
}

function DebugModeButton({ debugMode, setDebugMode }) {
    return (
        <button
            className={`debug-mode-toggle ${debugMode ? 'active' : ''}`}
            onClick={() => setDebugMode(!debugMode)}
            title="Debug Mode (Ctrl+Shift+D)"
        >
            {debugMode ? 'DEBUG: ON' : 'Debug: OFF'}
        </button>
    );
}

export default App;